import { apiJson } from '../api';
import type { SavedSearchRunState } from '../utils/savedSearchRunState';
import type { SavedSearchRunStatus } from './savedSearchRepository';

export interface TldrPromptSettings {
  decay_exponent: number | null;
  relevance_boost_multiplier: number | null;
  description?: string;
}

export interface TldrPromptUpdate {
  description: string;
  decay_exponent: number;
  relevance_boost_multiplier: number;
}

export interface TldrPreviewPayload {
  description: string;
  decay_exponent: number;
  relevance_boost_multiplier: number;
}

export interface TldrPreviewStart {
  search_id: string;
  title?: string | null;
  description_id: string;
  matrix_id: string;
  status: SavedSearchRunState;
  candidate_count: number;
}

/**
 * A TLDR preview is a TLDR-depth fast-search run under the hood: the backend serves
 * both status endpoints from the same payload builder
 * (`build_search_status_response`), so the shape has one owner. This used to
 * be a hand-copied interface that had silently fallen behind (it was missing
 * the relevant/not-relevant counters the payload carries).
 */
export type TldrPreviewStatus = SavedSearchRunStatus;

export interface TldrPromptStatus {
  prompt_updated_at: string | null;
  snapshot_refreshed_at: string | null;
  /** True once the live feed snapshot reflects the latest saved prompt. */
  reflects_latest_prompt: boolean;
}

export function getTldrPrompt(): Promise<TldrPromptSettings> {
  return apiJson<TldrPromptSettings>('/api/admin/tldr-prompt');
}

export function getTldrPromptStatus(): Promise<TldrPromptStatus> {
  return apiJson<TldrPromptStatus>('/api/admin/tldr-prompt/status');
}

export function updateTldrPrompt(update: TldrPromptUpdate): Promise<TldrPromptSettings> {
  return apiJson<TldrPromptSettings>('/api/admin/tldr-prompt', {
    method: 'PATCH',
    json: update,
  });
}

export function startTldrPreview(payload: TldrPreviewPayload): Promise<TldrPreviewStart> {
  return apiJson<TldrPreviewStart>('/api/admin/tldr-preview', {
    method: 'POST',
    json: payload,
  });
}

export function getTldrPreviewStatus(searchId: string): Promise<TldrPreviewStatus> {
  return apiJson<TldrPreviewStatus>(`/api/admin/tldr-preview/${encodeURIComponent(searchId)}`);
}

export function refreshTldrPreview(searchId: string): Promise<TldrPreviewStart> {
  return apiJson<TldrPreviewStart>(`/api/admin/tldr-preview/${encodeURIComponent(searchId)}/refresh`, {
    method: 'POST',
  });
}
